<template>
  <div class="recebimento-form">
    <!-- Read-only Info Banner -->
    <div v-if="readOnly" class="readonly-info-banner">
      <i class="pi pi-info-circle"></i>
      <span>{{ t('Este recebimento foi processado pelo provedor de pagamento e não pode ser editado manualmente.') }}</span>
    </div>

    <!-- Payment Method Box -->
    <box
      type="primary"
      :title="t('Forma de pagamento')"
      elem-id="boxFormaPagamentoRecebimento">

      <buttons-list
        data-testid="recebimento-forma-pagamento"
        :placeholder="t('Escolha a forma de pagamento')"
        :validation="() => isFormaPagamentoValid"
        :required="true"
        :disabled="readOnly"
        elem-id="recebimento_forma_pagamento_id"
        v-model="formData.recebimento_forma_pagamento_id"
        :itens-select="formasPagamentoOptions">
      </buttons-list>
    </box>

    <!-- Revenue Date Box -->
    <box
      type="primary"
      :title="t('Data do recebimento')"
      elem-id="boxDataRecebimento">

      <input-date
        elem-id="recebimento_data"
        data-testid="recebimento-data"
        :placeholder="t('Escolha a data')"
        :validation="() => isDataRecebimentoValid"
        :required="true"
        :disabled="readOnly"
        v-model="formData.recebimento_data">
      </input-date>
    </box>

    <!-- Value Box -->
    <box
      type="primary"
      :title="t('Valor')"
      elem-id="boxValorRecebimento">

      <div class="form-row">
        <div class="form-col">
          <input-currency
            elem-id="recebimento_valor"
            data-testid="recebimento-valor"
            :label="t('Valor')"
            :placeholder="t('0,00')"
            :validation="() => isValorValid"
            :required="true"
            :disabled="readOnly || ehParcela"
            v-model="valorDisplay">
          </input-currency>
          <small class="field-hint">{{ t('Valor pago pelo cliente') }}</small>
          <small
            v-if="atendenteOrOperador && valorRestanteCap !== null"
            class="field-hint"
            data-testid="recebimento-valor-max-hint">
            {{ t('Máximo: {valor} (saldo a receber)', { valor: formatCurrency(valorRestanteCap) }) }}
          </small>
        </div>
        <div class="form-col">
          <input-currency
            elem-id="recebimento_valor_taxas"
            data-testid="recebimento-valor-taxas"
            :label="t('Taxas')"
            :placeholder="t('0,00')"
            :validation="() => true"
            :required="false"
            :disabled="readOnly"
            v-model="valorTaxasDisplay">
          </input-currency>
          <small class="field-hint">{{ t('Taxas do processador de pagamento') }}</small>
        </div>
      </div>
      <div class="valor-liquido-display">
        <label>{{ t('Valor líquido') }}:</label>
        <span class="valor-liquido-value">{{ formatCurrency(valorLiquidoDisplay) }}</span>
        <small class="field-hint">{{ t('Valor menos taxas') }}</small>
      </div>

      <!-- Venda parcelada (chamado 2026-0067): escolha só na criação; na edição, a parcela é informada. -->
      <recebimento-parcelas-select
        v-if="!isEditMode"
        v-model="numeroParcelas"
        :max-parcelas="maxParcelas"
        :valor-centavos="formData.recebimento_valor || 0"
        :disabled="readOnly">
      </recebimento-parcelas-select>
      <div v-else-if="ehParcela" class="parcela-info" data-testid="recebimento-parcela-info">
        <i class="pi pi-info-circle"></i>
        <span>
          {{ tp('Parcela {i} de {n} desta venda.', { i: Number(formData.recebimento_parcela_numero ?? 1), n: Number(formData.recebimento_numero_parcelas ?? 1) }) }}
          {{ t('O valor de uma parcela não muda — para mudar a venda, exclua-a e registre de novo.') }}
        </span>
      </div>
    </box>

    <!-- Observations Box -->
    <box
      type="primary"
      :title="t('Observações')"
      elem-id="boxObservacoesRecebimento">

      <input-rich-text
        data-testid="recebimento-observacoes"
        :placeholder="t('Digite as observações')"
        :required="false"
        :disabled="readOnly"
        elem-id="recebimento_observacoes"
        v-model="formData.recebimento_observacoes">
      </input-rich-text>
    </box>

    <!-- Administrative Information (Full permissions only) -->
    <Accordion v-if="hasFullPermissions" class="admin-accordion">
      <AccordionTab :header="t('Informações administrativas')">
        <!-- Basic Information Box -->
        <box
          type="primary"
          :title="t('Informações básicas')"
          elem-id="boxInformacoesGeraisRecebimento">

          <div class="form-row">
            <div class="form-col">
              <input-date-time
                elem-id="recebimento_data_cadastro"
                data-testid="recebimento-data-cadastro"
                :label="t('Data de cadastro')"
                :placeholder="t('Data de cadastro')"
                :disabled="true"
                v-model="formData.recebimento_data_cadastro">
              </input-date-time>
            </div>
            <div class="form-col">
              <franchise-select
                :label="t('Franquia')"
                :placeholder="t('Escolha a franquia')"
                :disabled="true"
                :validation="() => isFranquiaValid"
                :required="true"
                elem-id="recebimento_franquia_id"
                v-model="formData.recebimento_franquia_id">
              </franchise-select>
            </div>
          </div>
        </box>

        <!-- Origin Box -->
        <box
          type="primary"
          :title="t('Origem')"
          elem-id="boxOrigemRecebimento">

          <div class="form-row">
            <div class="form-col">
              <buttons-list
                :label="t('Origem')"
                :placeholder="t('Escolha a origem')"
                :disabled="true"
                :validation="() => isOrigemValid"
                :required="true"
                elem-id="recebimento_origem"
                v-model="formData.recebimento_origem"
                :itens-select="origensOptions">
              </buttons-list>
            </div>
            <div class="form-col">
              <input-text
                elem-id="recebimento_origem_id"
                data-testid="recebimento-origem-id"
                :label="t('ID origem')"
                :placeholder="t('ID da origem')"
                :disabled="true"
                :validation="() => isOrigemIdValid"
                :required="true"
                v-model="origemIdDisplay">
              </input-text>
            </div>
          </div>
        </box>

        <!-- Registered By Box -->
        <!--
          AUTORIA — só leitura, e por isso sem `validation`/`required`: os dois
          campos são escritos pelo sistema e o usuário não tem como corrigi-los.
          Marcá-los como obrigatórios travava o formulário de recebimentos
          antigos sem autor (chamado 2026-0033).
        -->
        <box
          type="primary"
          :title="t('Registrado por')"
          elem-id="boxRegistradoPorRecebimento">

          <div class="form-row">
            <div class="form-col">
              <buttons-list
                :label="t('Registrado por')"
                :placeholder="t('Escolha quem registrou')"
                :disabled="true"
                elem-id="recebimento_registrado_por"
                v-model="formData.recebimento_registrado_por"
                :itens-select="registradoPorOptions">
              </buttons-list>
            </div>
            <div class="form-col">
              <input-text
                elem-id="recebimento_registrado_por_id"
                data-testid="recebimento-registrado-por-id"
                :label="t('ID registrado por')"
                :placeholder="t('ID registrado por')"
                :disabled="true"
                v-model="registradoPorIdDisplay">
              </input-text>
            </div>
          </div>
        </box>

        <!-- Provider Box -->
        <box
          type="primary"
          :title="t('Provedor')"
          elem-id="boxProvedorRecebimento">

          <div class="form-row">
            <div class="form-col">
              <input-text
                data-testid="recebimento-provedor"
                :label="t('Provedor')"
                :placeholder="t('Provedor')"
                :disabled="true"
                elem-id="recebimento_provedor"
                v-model="formData.recebimento_provedor">
              </input-text>
            </div>
            <div class="form-col">
              <input-text
                data-testid="recebimento-id-externo"
                :label="t('ID Externo')"
                :placeholder="t('ID Externo')"
                :disabled="true"
                elem-id="recebimento_id_externo"
                v-model="formData.recebimento_id_externo">
              </input-text>
            </div>
          </div>
        </box>
      </AccordionTab>
    </Accordion>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { tComParametros } from '@/utils/tComParametros';
import { useI18n } from 'vue-i18n';
import { useFormasPagamento } from '@/composables/useFormasPagamento';
import { parcelasMaxDaForma } from '@/composables/useParcelamento';
import type { FormasPagamento_FormaPagamentoRow } from '@/contracts/FormasPagamento_FormaPagamentoRow';
import { permissionsService, permissionsState } from '@/services/permissionsService';
import RecebimentoParcelasSelect from './RecebimentoParcelasSelect.vue';
import { LOCALE_TO_CURRENCY } from '@/utils/currencyMinorUnits';
import { log } from '@/utils/DebugLogger';

type Id = number | string;

interface RecebimentoFormData {
  recebimento_id?: Id;
  recebimento_data_cadastro?: string;
  recebimento_franquia_id?: number;
  recebimento_forma_pagamento_id?: number;
  recebimento_data?: string;
  recebimento_valor?: number;
  recebimento_valor_taxas?: number;
  recebimento_valor_liquido?: number;
  recebimento_observacoes?: string;
  recebimento_origem?: string;
  recebimento_origem_id?: Id;
  recebimento_registrado_por?: string;
  recebimento_registrado_por_id?: Id;
  recebimento_provedor?: string;
  recebimento_id_externo?: string;
  recebimento_numero_parcelas?: number;
  recebimento_parcela_numero?: number;
  recebimento_parcela_de?: number | null;
  [key: string]: unknown;
}

interface SelectOption {
  ID: Id;
  Name: string;
}


const { t } = useI18n();

// Chave nova com parâmetros: nunca mostrar "{n}" cru (utils/tComParametros).
const tp = (chave: string, parametros: Record<string, string | number>): string => tComParametros(t, chave, parametros);
const { listarFormasPagamento } = useFormasPagamento();

const props = withDefaults(defineProps<{
  modelValue: RecebimentoFormData;
  isEditMode?: boolean;
  readOnly?: boolean;
  /**
   * Maximum allowed `recebimento_valor` (in reais) for atendente/operador users.
   * Enforced only when `atendenteOrOperador === true`. Null means no cap.
   * Mirrors the backend gate in recebimentos.php::save.
   */
  valorRestanteCap?: number | null;
  /**
   * True when the active user is in Atendente or Operador (and NOT in
   * manager-or-above). Required because the Cognito group cascade means
   * a Full user is also in Atendente — the parent (RecebimentoModal) does
   * the exclusion via `useUserPermissions` and passes the resolved boolean.
   */
  atendenteOrOperador?: boolean;
}>(), {
  isEditMode: false,
  readOnly: false,
  valorRestanteCap: null,
  atendenteOrOperador: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: RecebimentoFormData];
}>();

// Form data
const formData = computed<RecebimentoFormData>({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

// recebimento_valor is ALWAYS stored in BRL centavos (minor units of BRL)
// regardless of the user locale. CLP/JPY have factor 1 but the DB wire
// value is BRL centavos — hard-code factor 100 so it decodes correctly
// for every locale.
const PRICE_FACTOR_BRL = 100;

// User permissions
const hasFullPermissions = ref<boolean>(false);

// Options for selects
const formasPagamento = ref<FormasPagamento_FormaPagamentoRow[]>([]);
const formasPagamentoOptions = ref<SelectOption[]>([]);

// Venda parcelada (chamado 2026-0067)
const formaSelecionada = computed(() =>
  formasPagamento.value.find((fp) => Number(fp.forma_pagamento_id) === Number(formData.value.recebimento_forma_pagamento_id)) ?? null
);
const maxParcelas = computed<number>(() => parcelasMaxDaForma(formaSelecionada.value));
const numeroParcelas = computed<number>({
  get: () => Number(formData.value.recebimento_numero_parcelas ?? 1),
  set: (n: number) => { formData.value.recebimento_numero_parcelas = n; },
});
/** Uma linha de venda parcelada (na edição): o valor dela é parte da venda e não se edita. */
const ehParcela = computed<boolean>(() => props.isEditMode && Number(formData.value.recebimento_numero_parcelas ?? 1) > 1);

const origensOptions = ref<SelectOption[]>([
  { ID: 'compromisso', Name: t('Compromisso') },
  { ID: 'so_order', Name: t('Ordem de Serviço') },
]);

const registradoPorOptions = ref<SelectOption[]>([
  { ID: 'user', Name: 'User' },
  { ID: 'hook', Name: 'Hook' }
]);

// Currency handling - convert between centavos and display value
// Valor (gross - what customer paid)
const valorDisplay = computed<number>({
  get: () => {
    // Convert minor units to major units for display
    return (formData.value.recebimento_valor || 0) / PRICE_FACTOR_BRL;
  },
  set: (value: number) => {
    // Convert major units to minor units for storage
    formData.value.recebimento_valor = Math.round(value * PRICE_FACTOR_BRL);
    // Auto-calculate valor_liquido (net)
    calculateValorLiquido();
  }
});

// Valor taxas (fees from payment processor)
const valorTaxasDisplay = computed<number>({
  get: () => {
    // Convert minor units to major units for display
    return (formData.value.recebimento_valor_taxas || 0) / PRICE_FACTOR_BRL;
  },
  set: (value: number) => {
    // Convert major units to minor units for storage
    formData.value.recebimento_valor_taxas = Math.round(value * PRICE_FACTOR_BRL);
    // Auto-calculate valor_liquido (net)
    calculateValorLiquido();
  }
});

// String displays for disabled input-text fields (Id can be number; component requires string)
const origemIdDisplay = computed<string>({
  get: () => String(formData.value.recebimento_origem_id ?? ''),
  set: (value: string) => {
    formData.value.recebimento_origem_id = value;
  },
});

const registradoPorIdDisplay = computed<string>({
  get: () => String(formData.value.recebimento_registrado_por_id ?? ''),
  set: (value: string) => {
    formData.value.recebimento_registrado_por_id = value;
  },
});

// Valor liquido (net = valor - taxas) - read only
const valorLiquidoDisplay = computed<number>(() => {
  const valor = formData.value.recebimento_valor || 0;
  const taxas = formData.value.recebimento_valor_taxas || 0;
  return (valor - taxas) / PRICE_FACTOR_BRL;
});

// Calculate and update valor_liquido (net) when valor or taxas change
const calculateValorLiquido = (): void => {
  const valor = formData.value.recebimento_valor || 0;
  const taxas = formData.value.recebimento_valor_taxas || 0;
  formData.value.recebimento_valor_liquido = valor - taxas;
};

// Format currency for display using the active user locale
const formatCurrency = (value: number | undefined): string => {
  const locale = (permissionsState as { normalizedLocale?: string })?.normalizedLocale || 'pt-BR';
  const currency = LOCALE_TO_CURRENCY[locale] || 'BRL';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency
  }).format(value || 0);
};

// Validation
const isFranquiaValid = computed<boolean>(() => {
  return !!formData.value.recebimento_franquia_id && formData.value.recebimento_franquia_id > 0;
});

const isFormaPagamentoValid = computed<boolean>(() => {
  return !!formData.value.recebimento_forma_pagamento_id && formData.value.recebimento_forma_pagamento_id > 0;
});

const isValorValid = computed<boolean>(() => {
  // Gross value (valor) must be positive, net value (valor_liquido) can be 0 or positive
  const valor = formData.value.recebimento_valor ?? 0;
  if (valor <= 0) return false;
  // Cannot exceed the saldo a receber (valorRestanteCap). Cap is in major units
  // (reais); valor is in minor units — convert the cap with the active factor.
  // ROUNDED, like the modal rounds the prefilled valor: 142.89 * 100 is
  // 14288.999… in float, and the saldo itself "exceeded" its own cap
  // (chamado 2026-0001 — Salvar desabilitado com o valor pré-preenchido).
  if (props.valorRestanteCap !== null && props.valorRestanteCap !== undefined) {
    if (valor > Math.round(props.valorRestanteCap * PRICE_FACTOR_BRL)) return false;
  }
  return true;
});

const isOrigemValid = computed<boolean>(() => {
  return !!formData.value.recebimento_origem;
});

const isOrigemIdValid = computed<boolean>(() => {
  const id = formData.value.recebimento_origem_id;
  return !!id && Number(id) > 0;
});

const isDataRecebimentoValid = computed<boolean>(() => {
  if (!formData.value.recebimento_data) return false;

  /*
   * COMPARAÇÃO EM TEXTO, não em Date — a guarda de data futura não funcionava
   * no Brasil (2026-09-09).
   *
   * `new Date('2026-09-10')` (só data, sem hora) é interpretado como MEIA-NOITE
   * UTC, que em GMT-3 é 21:00 do dia ANTERIOR. Comparado contra um "hoje" local
   * às 23:59:59, amanhã sempre passava: o recebimento podia ser salvo com data
   * futura, que é exatamente o que esta validação existe para impedir.
   *
   * Duas strings YYYY-MM-DD comparam lexicograficamente na ordem certa e não
   * têm fuso, então não há conversão que possa errar. `toISOString()` não serve
   * para montar o "hoje": ela devolve UTC e traria o mesmo erro de volta.
   */
  const selecionada = String(formData.value.recebimento_data).slice(0, 10);
  if (selecionada.length !== 10) return false;

  const agora = new Date();
  const hoje = `${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, '0')}-${String(agora.getDate()).padStart(2, '0')}`;

  return selecionada <= hoje;
});

// Form is valid if all required fields are valid
/**
 * O que impede de salvar, em português — uma linha por campo inválido.
 *
 * Existe porque o botão desabilitado sozinho não informa nada: a franqueada de
 * Vitacura trocou a forma de pagamento, viu "Salvar" apagado e concluiu que a
 * plataforma não aceitava crédito (chamado 2026-0033). O campo que barrava era
 * outro, e sequer aparece na tela.
 */
const pendencias = computed<string[]>(() => {
  const faltando: string[] = [];

  if (!isFormaPagamentoValid.value) faltando.push(t('Escolha a forma de pagamento'));
  if (!isValorValid.value) faltando.push(t('Informe um valor maior que zero e dentro do saldo a receber'));
  if (!isDataRecebimentoValid.value) faltando.push(t('Informe a data do recebimento — ela não pode ser futura'));
  if (!isFranquiaValid.value) faltando.push(t('Selecione a franquia'));
  if (!isOrigemValid.value || !isOrigemIdValid.value) faltando.push(t('Este recebimento não está vinculado a uma origem válida'));

  return faltando;
});

/**
 * Válido = nenhuma pendência.
 *
 * `recebimento_registrado_por` e `..._por_id` NÃO entram aqui, de propósito: são
 * a autoria do registro, escrita pelo sistema (`utils/recebimentoAutoria.ts`) e
 * invisível na tela. Enquanto entravam, todo recebimento gravado sem autor —
 * o que o modal de entrega do PDV fazia — ficava impossível de editar para
 * sempre, sem nenhuma explicação. Um campo que o usuário não vê nem pode
 * corrigir não pode desabilitar o botão dele; quem manda nesses campos é o
 * backend, que aceita a edição normalmente.
 *
 * `recebimento_data_cadastro` saiu pela MESMA razão (2026-09-09). É a data em
 * que a linha foi criada — auditoria do sistema, `disabled` na tela e ausente do
 * payload de save, ou seja: o usuário não pode preencher, e salvar não a
 * escreveria de qualquer forma. 45 das 171 linhas em homologação têm NULL nesse
 * campo (as de origem `so_order`, inseridas por caminho programático), e cada
 * uma delas ficava impossível de editar com a mensagem "Informe a data de
 * cadastro" apontando para um campo cinza que ninguém consegue informar.
 *
 * A regra geral, agora escrita duas vezes: só entra em `pendencias` o que o
 * usuário vê, consegue mudar e o save realmente envia.
 */
const isFormValid = computed<boolean>(() => pendencias.value.length === 0);

// Expose validation state and data for parent components
defineExpose({
  isValorValid,
  isFormValid,
  pendencias
});

// Load formas de pagamento
const loadFormasPagamento = async (): Promise<void> => {
  try {
    log('RecebimentoForm', 'loadFormasPagamento', 'Loading payment methods');
    const franquiaId = formData.value.recebimento_franquia_id;
    const lista = await listarFormasPagamento(false, franquiaId) as FormasPagamento_FormaPagamentoRow[];
    if (franquiaId !== formData.value.recebimento_franquia_id) return; // a franquia mudou enquanto carregava
    formasPagamento.value = lista;

    // Transform to buttons-list format
    formasPagamentoOptions.value = lista.map((fp: FormasPagamento_FormaPagamentoRow) => ({
      ID: fp.forma_pagamento_id,
      Name: fp.forma_pagamento_nome
    }));

    log('RecebimentoForm', 'loadFormasPagamento', 'Loaded payment methods', formasPagamentoOptions.value.length);

    // Auto-select first forma de pagamento if none selected
    if (!formData.value.recebimento_forma_pagamento_id && formasPagamentoOptions.value.length > 0) {
      formData.value.recebimento_forma_pagamento_id = Number(formasPagamentoOptions.value[0].ID);
    }
  } catch (error) {
    log('RecebimentoForm', 'loadFormasPagamento', 'Error loading payment methods', error);
  }
};

// A forma vale por franquia (chamado 2026-0023): parcelas e prazo dependem de qual é.
watch(() => formData.value.recebimento_franquia_id, loadFormasPagamento);

// Initialize
onMounted(() => {
  log('RecebimentoForm', 'onMounted', 'Initializing form');

  // Check user permissions
  const userGroups = permissionsService.getUserGroups();
  hasFullPermissions.value = userGroups.includes('Full');

  // Load formas de pagamento
  loadFormasPagamento();
});
</script>

<style scoped>
.recebimento-form {
  padding: 0;
}

/* Read-only info banner */
.readonly-info-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  margin-bottom: 16px;
  background-color: #e7f3ff;
  border: 1px solid #b8daff;
  border-radius: 6px;
  color: #004085;
  font-size: 0.9rem;
}

.readonly-info-banner i {
  font-size: 1.1rem;
  flex-shrink: 0;
}

/* Flexbox form layout - replaces Bootstrap grid */
.form-row {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.form-col {
  flex: 1 1 calc(50% - 10px);
  min-width: 250px;
}

/* Responsive: Stack columns on tablets and mobile */
@media (max-width: 992px) {
  .form-row {
    flex-direction: column;
  }

  .form-col {
    flex: 1 1 100%;
  }
}

.dl-horizontal dd {
  margin-bottom: 10px;
}

/* Venda parcelada: aviso da parcela na edição */
.parcela-info {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 12px;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 0.85rem;
  color: #495057;
}

/* Admin accordion styling */
.admin-accordion {
  margin-top: 20px;
}

.admin-accordion :deep(.p-accordion-header-link) {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  color: #6c757d;
  font-weight: 500;
}

.admin-accordion :deep(.p-accordion-header-link:hover) {
  background-color: #e9ecef;
}

.admin-accordion :deep(.p-accordion-content) {
  padding: 1rem 0;
  background-color: #ffffff;
  border: 1px solid #dee2e6;
  border-top: none;
}

/* Field hints */
.field-hint {
  display: block;
  color: #6c757d;
  font-size: 0.8rem;
  margin-top: 4px;
}

/* Valor liquido display */
.valor-liquido-display {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 6px;
  margin-top: 15px;
  flex-wrap: wrap;
}

.valor-liquido-display label {
  font-weight: 600;
  color: #495057;
  margin: 0;
}

.valor-liquido-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #28a745;
}

.valor-liquido-display .field-hint {
  flex-basis: 100%;
  margin-top: 5px;
}
</style>
